import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { auth } from '../firebase';

export interface EdvisorButton {
  id: string;
  label: string;
}

export interface EdvisorProgram {
  name: string;
  id: string;
  url: string;
}

export interface EdvisorSchool {
  name: string;
  id: string;
  url: string;
}

export interface EdvisorMessage {
  id: string;
  content: string;
  sender: 'user' | 'edvisor';
  timestamp: string;
  threadId?: string;
  buttons?: EdvisorButton[];
  programs?: EdvisorProgram[];
  schools?: EdvisorSchool[];
  eligibilityData?: {
    isEligible: boolean | null;
    isOnRightPath?: string;
    calculatedAverage?: number;
    programAverage?: number;
    missingCourses?: string[];
    gradeIssues?: string[];
    alertMessage?: string;
    nextYearSuggestion?: string;
    coursesUsedInAverage?: string[];
  };
  allProgramsEligibility?: { [programId: string]: any };
}

export interface EdvisorChatRequest {
  message: string;
  threadId?: string;
}

export interface EdvisorChatResponse {
  response: string;
  buttons?: EdvisorButton[];
  programs?: EdvisorProgram[];
  schools?: EdvisorSchool[];
  threadId: string;
  timestamp: string;
  eligibilityData?: {
    isEligible: boolean | null;
    isOnRightPath?: string;
    calculatedAverage?: number;
    programAverage?: number;
    missingCourses?: string[];
    gradeIssues?: string[];
    alertMessage?: string;
    nextYearSuggestion?: string;
    coursesUsedInAverage?: string[];
  };
  allProgramsEligibility?: { [programId: string]: any };
}

export interface ChatThread {
  threadId: string;
  title: string;
  lastMessage: string;
  timestamp: string;
  messageCount: number;
  isActive: boolean;
}

export interface ThreadMetadata {
  title: string;
  lastMessage: string;
  lastMessageTimestamp: string;
  messageCount: number;
  createdAt: string;
}


class EdvisorService {
  // Using main callable function with Brian's fixes
  private chatFunction = httpsCallable(functions, 'edvisorChatResponses');

  /**
   * Send a message to EdVisor and get a response
   * Now using LangGraph implementation for improved workflow management
   */
  async sendMessage(request: EdvisorChatRequest): Promise<EdvisorChatResponse> {
    try {
      // Debug: Check current user authentication
      const currentUser = auth.currentUser;
      console.log('🔍 Debug - Current user before main function call:', {
        uid: currentUser?.uid,
        email: currentUser?.email,
        isAnonymous: currentUser?.isAnonymous,
        hasToken: !!currentUser
      });

      // Ensure user is authenticated
      if (!currentUser) {
        throw new Error('User must be authenticated to use EdVisor chat');
      }

      // Wait for auth token to be ready
      const token = await currentUser.getIdToken();
      console.log('🔍 Debug - Auth token obtained:', !!token);

      // Debug: Log the request
      console.log('🔍 Debug - EdVisor Main API request:', request);
      console.log('🔍 Debug - Chat function object:', this.chatFunction);

      const result = await this.chatFunction(request);
      console.log('🔍 Debug - EdVisor Main API response:', result.data);
      return result.data as EdvisorChatResponse;
    } catch (error: any) {
      console.error('❌ Error sending message to EdVisor:', error);
      console.error('❌ Full error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      throw new Error(error.message || 'Failed to send message to EdVisor');
    }
  }


  /**
   * Get the current active thread ID for a user
   */
  async getActiveThread(): Promise<string | null> {
    try {
      const { database } = await import('../firebase');
      const { ref, get } = await import('firebase/database');
      const { auth } = await import('../firebase');
      
      const user = auth.currentUser;
      if (!user) return null;
      
      const activeThreadRef = ref(database, `edvisor/${user.uid}/activeThread`);
      const snapshot = await get(activeThreadRef);
      
      return snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Error getting active thread:', error);
      return null;
    }
  }

  /**
   * Load chat history from database
   */
  async loadChatHistory(threadId?: string): Promise<EdvisorMessage[]> {
    try {
      const { database } = await import('../firebase');
      const { ref, get } = await import('firebase/database');
      const { auth } = await import('../firebase');
      
      const user = auth.currentUser;
      if (!user) return [];
      
      // Get active thread if no threadId provided
      const activeThreadId = threadId || await this.getActiveThread();
      if (!activeThreadId) return [];
      
      // Load messages from database
      const chatRef = ref(database, `edvisor/${user.uid}/${activeThreadId}`);
      const snapshot = await get(chatRef);
      
      if (!snapshot.exists()) return [];
      
      const threadData = snapshot.val();
      const messages = threadData.messages || [];
      
      // Convert database messages to EdvisorMessage format
      return messages.map((msg: any, index: number) => {
        return {
          id: `${msg.timestamp}-${index}`,
          content: msg.message,
          sender: msg.role === 'assistant' ? 'edvisor' : 'user',
          timestamp: msg.timestamp,
          threadId: activeThreadId,
          // Note: buttons, programs, and schools are now parsed in the EdvisorMessage component
          // using the token-based approach, so we don't need to parse them here
        };
      });
    } catch (error) {
      console.error('Error loading chat history:', error);
      return [];
    }
  }

  /**
   * Create a formatted message object
   * Note: buttons, programs, and schools are now parsed in the EdvisorMessage component
   * using the token-based approach, so we only need to store the raw content
   */
  createMessage(
    content: string, 
    sender: 'user' | 'edvisor', 
    threadId?: string,
    eligibilityData?: any,
    allProgramsEligibility?: { [programId: string]: any },
    buttons?: EdvisorButton[]
  ): EdvisorMessage {
    return {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content,
      sender,
      timestamp: new Date().toISOString(),
      threadId,
      eligibilityData,
      allProgramsEligibility,
      buttons
    };
  }

  /**
   * Rename a chat thread
   */
  async renameThread(threadId: string, newTitle: string): Promise<void> {
    try {
      const { database } = await import('../firebase');
      const { ref, update } = await import('firebase/database');
      const { auth } = await import('../firebase');
      
      const user = auth.currentUser;
      if (!user) throw new Error('User must be authenticated');
      
      if (!newTitle.trim()) {
        throw new Error('Thread title cannot be empty');
      }
      
      const threadRef = ref(database, `edvisor/${user.uid}/${threadId}`);
      await update(threadRef, {
        title: newTitle.trim()
      });
      
      console.log('✅ EdvisorService: Renamed thread', threadId, 'to', newTitle);
    } catch (error) {
      console.error('❌ Error renaming thread:', error);
      throw error;
    }
  }

  /**
   * Delete (hide) a chat thread
   */
  async deleteThread(threadId: string): Promise<void> {
    try {
      const { database } = await import('../firebase');
      const { ref, update } = await import('firebase/database');
      const { auth } = await import('../firebase');
      
      const user = auth.currentUser;
      if (!user) throw new Error('User must be authenticated');
      
      const threadRef = ref(database, `edvisor/${user.uid}/${threadId}`);
      await update(threadRef, {
        isHidden: true,
        hiddenAt: new Date().toISOString()
      });
      
      console.log('✅ EdvisorService: Hidden thread', threadId);
    } catch (error) {
      console.error('❌ Error hiding thread:', error);
      throw error;
    }
  }

  /**
   * Get all chat threads for the current user
   */
  async getAllUserThreads(): Promise<ChatThread[]> {
    try {
      const { database } = await import('../firebase');
      const { ref, get } = await import('firebase/database');
      const { auth } = await import('../firebase');
      
      const user = auth.currentUser;
      if (!user) return [];
      
      const activeThreadId = await this.getActiveThread();
      const userThreadsRef = ref(database, `edvisor/${user.uid}`);
      const snapshot = await get(userThreadsRef);
      
      if (!snapshot.exists()) return [];
      
      const userData = snapshot.val();
      const threads: ChatThread[] = [];
      
      // Iterate through all threads (excluding activeThread)
      for (const [threadId, threadData] of Object.entries(userData)) {
        if (threadId === 'activeThread') continue;
        
        const metadata = threadData as any;
        const messages = metadata.messages || [];
        
        // Skip hidden threads
        if (metadata.isHidden) continue;
        
        if (messages.length === 0) continue;
        
        // Use existing title or generate from first user message (Option A)
        let title = metadata.title;
        if (!title) {
          const firstUserMessage = messages.find((msg: any) => msg.role === 'user');
          title = firstUserMessage 
            ? this.generateThreadTitle(firstUserMessage.message)
            : 'Untitled Chat';
        }
        
        // Get last message and clean up formatting tokens
        const lastMessage = messages[messages.length - 1];
        const lastMessageText = this.cleanMessageText(lastMessage?.message || '');
        
        // Get timestamp from last message or use lastUpdated
        const timestamp = lastMessage?.timestamp || metadata.lastUpdated || metadata.createdAt || new Date().toISOString();
        
        threads.push({
          threadId,
          title,
          lastMessage: lastMessageText,
          timestamp: new Date(timestamp).toISOString(), // Ensure ISO format
          messageCount: messages.length,
          isActive: threadId === activeThreadId,
        });
      }
      
      // Sort by timestamp (newest first)
      threads.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      // Limit to 6 most recent threads
      const limitedThreads = threads.slice(0, 6);
      
      console.log('✅ EdvisorService: Loaded', limitedThreads.length, 'chat threads (limited to 6)');
      console.log('🔍 Debug - Thread details:', limitedThreads.map(t => ({
        threadId: t.threadId,
        title: t.title,
        messageCount: t.messageCount,
        isActive: t.isActive
      })));
      return limitedThreads;
    } catch (error) {
      console.error('❌ Error getting user threads:', error);
      return [];
    }
  }

  /**
   * Load messages from a specific thread
   */
  async loadSpecificThread(threadId: string): Promise<EdvisorMessage[]> {
    try {
      const { database } = await import('../firebase');
      const { ref, get, set } = await import('firebase/database');
      const { auth } = await import('../firebase');
      
      const user = auth.currentUser;
      if (!user) return [];
      
      // Load messages from specific thread
      const chatRef = ref(database, `edvisor/${user.uid}/${threadId}`);
      const snapshot = await get(chatRef);
      
      if (!snapshot.exists()) return [];
      
      const threadData = snapshot.val();
      const messages = threadData.messages || [];
      
      // Set this thread as active
      const activeThreadRef = ref(database, `edvisor/${user.uid}/activeThread`);
      await set(activeThreadRef, threadId);
      
      // Convert database messages to EdvisorMessage format
      const edvisorMessages = messages.map((msg: any, index: number) => {
        return {
          id: `${msg.timestamp}-${index}`,
          content: msg.message,
          sender: msg.role === 'assistant' ? 'edvisor' : 'user',
          timestamp: msg.timestamp,
          threadId: threadId,
        };
      });
      
      console.log('✅ EdvisorService: Loaded', edvisorMessages.length, 'messages from thread:', threadId);
      return edvisorMessages;
    } catch (error) {
      console.error('❌ Error loading specific thread:', error);
      return [];
    }
  }

  /**
   * Save thread metadata when a new message is sent
   */
  async saveThreadMetadata(threadId: string, userMessage: string, edvisorResponse: string): Promise<void> {
    try {
      const { database } = await import('../firebase');
      const { ref, get, set } = await import('firebase/database');
      const { auth } = await import('../firebase');
      
      const user = auth.currentUser;
      if (!user) return;
      
      const threadRef = ref(database, `edvisor/${user.uid}/${threadId}`);
      const snapshot = await get(threadRef);
      
      if (!snapshot.exists()) {
        console.log('⚠️ Thread does not exist yet, skipping metadata save');
        return;
      }
      
      const threadData = snapshot.val();
      const messages = threadData.messages || [];
      
      // Generate title from first user message if this is a new thread
      let title = threadData.title;
      if (!title && messages.length <= 2 && userMessage) { // First user message + first edvisor response
        title = this.generateThreadTitle(userMessage);
      }
      
      // Ensure title is never undefined
      const finalTitle = title || threadData.title || 'Untitled Chat';
      
      // Update thread metadata - preserve existing data structure
      const updatedThreadData = {
        ...threadData,
        title: finalTitle,
        lastMessage: edvisorResponse,
        lastMessageTimestamp: new Date().toISOString(),
        messageCount: messages.length,
        updatedAt: new Date().toISOString(),
        // Preserve existing fields from Firebase function
        lastUpdated: threadData.lastUpdated,
        lastResponseId: threadData.lastResponseId,
      };
      
      await set(threadRef, updatedThreadData);
      console.log('✅ EdvisorService: Saved thread metadata for:', threadId, {
        title: updatedThreadData.title,
        messageCount: updatedThreadData.messageCount
      });
    } catch (error) {
      console.error('❌ Error saving thread metadata:', error);
    }
  }

  /**
   * Clean message text by removing formatting tokens
   */
  private cleanMessageText(message: string): string {
    if (!message) return '';
    
    // Remove formatting tokens like ![S|...], ![P|...], etc.
    let cleanMessage = message
      .replace(/!\[S\|[^\]]*\]/g, '') // Remove school tokens
      .replace(/!\[P\|[^\]]*\]/g, '') // Remove program tokens
      .replace(/!\[A\|[^\]]*\]/g, '') // Remove athletics tokens
      .replace(/!\[E\|[^\]]*\]/g, '') // Remove event tokens
      .replace(/!\[[^\]]*\]/g, '')    // Remove any other formatting tokens
      .trim();
    
    // If the message is empty after cleaning, return a fallback
    if (!cleanMessage) {
      return 'Message with formatted content';
    }
    
    return cleanMessage;
  }

  /**
   * Generate a thread title from the first user message
   */
  private generateThreadTitle(firstMessage: string): string {
    // Handle null/undefined input
    if (!firstMessage || typeof firstMessage !== 'string') {
      return 'Untitled Chat';
    }
    
    // Clean up the message and truncate to reasonable length
    const cleanMessage = firstMessage.trim();
    const maxLength = 40;
    
    if (cleanMessage.length <= maxLength) {
      return cleanMessage;
    }
    
    // Find a good break point (end of word)
    const truncated = cleanMessage.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > 20) { // Don't break too early
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    return truncated + '...';
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

}

// Export singleton instance
export const edvisorService = new EdvisorService();
export default edvisorService;
